import { useState, memo } from 'react';
import {
  ChevronDown,
  Plane,
  PlaneTakeoff,
  Bed,
  MapPin,
  Edit3,
  Utensils,
  Ticket,
  Music,
  ShoppingBag,
} from 'lucide-react';
import ItemRow from './ItemRow';
import DayWarnings from './DayWarnings';
import { getParkDisplayName, getResultStateBadge, formatTime } from './helpers';

/**
 * Renders a single day card for a V3 itinerary day.
 * Handles arrival/departure/rest days and park days.
 */
function ItineraryDayCard({ day, dayNumber, onEditDay, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { dayType, parkKey, secondaryPark, items = [], message, mealSuggestions = [] } = day;
  const isNonParkDay = dayType === 'arrival' || dayType === 'departure' || dayType === 'rest';
  const isHopper = dayType === 'hopper' || !!secondaryPark;

  // Compute quick stats for the collapsed preview
  const rides = items.filter((i) => i.type === 'ride').length;
  const shows = items.filter((i) => i.type === 'show').length;
  const dining = items.filter((i) => i.type === 'dining').length;
  const resultBadge = getResultStateBadge(day.resultState);

  // Day title
  const parkName = getParkDisplayName(parkKey);
  const title = isNonParkDay
    ? parkName
    : isHopper
    ? `${getParkDisplayName(parkKey)} + ${getParkDisplayName(secondaryPark)}`
    : parkName;

  // Date display
  const dateStr = day.dateISO
    ? new Date(day.dateISO + 'T12:00:00').toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : '';

  // Time range
  const firstItem = items.find((i) => i.startMin != null);
  const lastItem = [...items].reverse().find((i) => i.endMin != null);
  const timeRange =
    firstItem && lastItem
      ? `${formatTime(firstItem.startMin)} - ${formatTime(lastItem.endMin)}`
      : '';

  // Day type icon
  const DayIcon =
    dayType === 'arrival' ? Plane :
    dayType === 'departure' ? PlaneTakeoff :
    dayType === 'rest' ? Bed :
    MapPin;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex gap-3 items-center px-5 py-4 w-full text-left transition-colors hover:bg-gray-50"
      >
        {/* Day number pill */}
        <div className="flex flex-shrink-0 justify-center items-center w-9 h-9 text-sm font-bold text-white bg-gray-900 rounded-lg">
          {dayNumber}
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center">
            <DayIcon className="flex-shrink-0 w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
            {isHopper && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium text-teal-700 bg-teal-50 rounded border border-teal-200">
                Hopper
              </span>
            )}
            {resultBadge && (
              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${resultBadge.style}`}>
                {resultBadge.label}
              </span>
            )}
          </div>
          <div className="flex gap-3 items-center mt-0.5">
            {dateStr && <span className="text-xs text-gray-500">{dateStr}</span>}
            {timeRange && !isNonParkDay && (
              <span className="text-xs text-gray-400">{timeRange}</span>
            )}
          </div>
        </div>

        {/* Quick stats (collapsed only) */}
        {!expanded && !isNonParkDay && items.length > 0 && (
          <div className="hidden gap-2 items-center sm:flex">
            {rides > 0 && (
              <span className="flex gap-1 items-center px-2 py-0.5 text-[10px] font-medium text-blue-700 bg-blue-50 rounded-full border border-blue-200">
                <Ticket className="w-3 h-3" /> {rides}
              </span>
            )}
            {shows > 0 && (
              <span className="flex gap-1 items-center px-2 py-0.5 text-[10px] font-medium text-violet-700 bg-violet-50 rounded-full border border-violet-200">
                <Music className="w-3 h-3" /> {shows}
              </span>
            )}
            {dining > 0 && (
              <span className="flex gap-1 items-center px-2 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 rounded-full border border-amber-200">
                <Utensils className="w-3 h-3" /> {dining}
              </span>
            )}
          </div>
        )}

        {/* Edit button */}
        {!isNonParkDay && onEditDay && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEditDay(day);
            }}
            className="flex-shrink-0 p-1.5 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
            title="Edit Day"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        )}

        {/* Chevron */}
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          {/* Non-park day: message + meal suggestions */}
          {isNonParkDay ? (
            <div className="pt-4">
              {message && (
                <p className="mb-3 text-sm leading-relaxed text-gray-600 italic">{message}</p>
              )}
              {mealSuggestions.length > 0 && (
                <div className="space-y-2">
                  {mealSuggestions.map((meal, i) => (
                    <div key={i} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                      <div className="flex gap-1.5 items-center mb-1">
                        <Utensils className="w-3 h-3 text-gray-500" />
                        <span className="text-xs font-medium text-gray-700 capitalize">
                          {meal.mealType} Suggestion
                        </span>
                      </div>
                      {meal.note && <p className="mb-2 text-xs text-gray-500">{meal.note}</p>}
                      {meal.suggestions?.map((s, j) => (
                        <div key={j} className="flex gap-2 items-center text-sm text-gray-900">
                          <span>{s.name}</span>
                          {s.location && (
                            <span className="text-xs text-gray-400">at {s.location}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {!message && mealSuggestions.length === 0 && (
                <p className="text-sm text-gray-400">No details for this day.</p>
              )}
            </div>
          ) : (
            /* Park day: timeline */
            <div className="pt-3 space-y-1.5">
              {items.length === 0 ? (
                <p className="py-4 text-sm text-center text-gray-400">No activities scheduled.</p>
              ) : (
                items.map((item, i) => (
                  <ItemRow
                    key={`${item.startMin}-${item.name}-${i}`}
                    item={item}
                    compact={item.type === 'buffer' && item.durationMin <= 5}
                  />
                ))
              )}
              <DayWarnings day={day} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(ItineraryDayCard);
